import { useState, useRef } from 'react';
import { PreviewPanel } from './PreviewPanel';
import { TimelinePanel } from './TimelinePanel';
import { EditorHeader } from './EditorHeader';
import { MaterialPanel } from './MaterialPanel';
import { ResizableDivider } from './ResizableDivider';
import { EffectClipDialog } from './EffectClipDialog';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import { useEffectsSync } from '../hooks/useEffectsSync';
import { useAutoSave } from '../hooks/useAutoSave';

const DEFAULT_TIMELINE_HEIGHT = 240;

export function EditorLayout() {
  useKeyboardShortcuts();
  useEffectsSync();
  useAutoSave();

  const [timelineHeight, setTimelineHeight] = useState(DEFAULT_TIMELINE_HEIGHT);
  const [timelineCollapsed, setTimelineCollapsed] = useState(false);
  const [prevTimelineHeight, setPrevTimelineHeight] = useState(DEFAULT_TIMELINE_HEIGHT);
  const editorAreaRef = useRef<HTMLDivElement>(null);

  const handleToggleTimeline = () => {
    if (timelineCollapsed) {
      setTimelineHeight(prevTimelineHeight);
      setTimelineCollapsed(false);
    } else {
      setPrevTimelineHeight(timelineHeight);
      setTimelineHeight(0);
      setTimelineCollapsed(true);
    }
  };

  return (
    <div
      className="flex overflow-hidden bg-[var(--user-page-bg)] text-[var(--user-page-text)]"
      style={{
        margin: '-1.5rem -2rem',
        height: 'calc(100vh - var(--view-as-offset, 0px))',
      }}
    >
      {/* Material Panel (sidebar) */}
      <MaterialPanel />

      {/* Editor Area: header + preview + divider + timeline */}
      <div ref={editorAreaRef} className="flex-1 flex flex-col min-w-0">
        <EditorHeader />

        {/* Preview */}
        <div className="flex-1 p-2 min-w-0 min-h-0">
          <PreviewPanel />
        </div>

        {/* Resizable divider */}
        <ResizableDivider
          timelineHeight={timelineHeight}
          onResize={setTimelineHeight}
          collapsed={timelineCollapsed}
          onToggleCollapse={handleToggleTimeline}
          containerRef={editorAreaRef}
        />

        {/* Timeline */}
        {!timelineCollapsed && (
          <div className="shrink-0" style={{ height: timelineHeight }}>
            <TimelinePanel />
          </div>
        )}
      </div>

      {/* Effect clip dialog (add/edit) */}
      <EffectClipDialog />
    </div>
  );
}
